import {t} from 'i18next';
import React from 'react';
import {Icon} from '@iconify/react';
import {Item, Menu, RightSlot, Separator, useContextMenu} from 'react-contexify';
import {Editor} from '@tiptap/react';
import {useDispatch, useStore} from 'react-redux';
import classNames from 'classnames';
import {IElement} from '../../../incase-domain/elements';
import {ELEMENT} from '../../../incase-domain/elements/base/ElementTypes';
import {MENU_IDS} from '../../../constants/menuIds';
import {SlideElementHotkeys} from '../../../constants/slideElementHotkeys';
import {AppState} from '../../../store/reducer';
import {editorSelector} from '../../../store/selectors/tiptapEditor.selectors';
import useElementActions from '../../../utils/hooks/slideUpdates/useElementActions';
import {createKeyMatcher, formatShortcuts} from '../../../incase-domain/components/contextMenu/ContextMenu';
import {setContextSelectedElementIndex} from '../../../store/actions/slide';

export interface ElementMenuProps {
    element: IElement;
    elementIndex: number;
    inHighlightedMode?: boolean;
}

const FIT_CONTENT_TYPES: ELEMENT[] = [
    ELEMENT.QUESTION,
    ELEMENT.MULTI_SELECT,
    ELEMENT.PHOTO_ANSWER_QUESTION,
    ELEMENT.MATCH_PAIR_QUESTION,
    ELEMENT.IMAGE_MARKUP,
    ELEMENT.SEQUENCE_QUESTION,
    ELEMENT.LIKERT_SCALE,
];

function isTableV2(p: ElementMenuProps | undefined): boolean {
    return p?.element.type === ELEMENT.STATIC_TABLE_V2;
}

type TableMenuLabelKey =
    | 'contextMenuTable.addRowBefore'
    | 'contextMenuTable.addRowAfter'
    | 'contextMenuTable.addColumnBefore'
    | 'contextMenuTable.addColumnAfter'
    | 'contextMenuTable.deleteRow'
    | 'contextMenuTable.deleteColumn'
    | 'contextMenuTable.mergeCells'
    | 'contextMenuTable.splitCell'
    | 'contextMenuTable.toggleHeaderRow'
    | 'contextMenuTable.toggleHeaderColumn';

type TableMenuCommand = {
    can: (ed: Editor) => boolean;
    run: (ed: Editor) => void;
    icon: string;
    labelKey: TableMenuLabelKey;
};

const TABLE_CONTEXT_MENU_GROUPS: TableMenuCommand[][] = [
    [
        {
            can: (ed) => ed.can().addRowBefore(),
            run: (ed) => ed.chain().focus().addRowBefore().run(),
            icon: 'tabler:row-insert-top',
            labelKey: 'contextMenuTable.addRowBefore',
        },
        {
            can: (ed) => ed.can().addRowAfter(),
            run: (ed) => ed.chain().focus().addRowAfter().run(),
            icon: 'tabler:row-insert-bottom',
            labelKey: 'contextMenuTable.addRowAfter',
        },
        {
            can: (ed) => ed.can().addColumnBefore(),
            run: (ed) => ed.chain().focus().addColumnBefore().run(),
            icon: 'tabler:column-insert-left',
            labelKey: 'contextMenuTable.addColumnBefore',
        },
        {
            can: (ed) => ed.can().addColumnAfter(),
            run: (ed) => ed.chain().focus().addColumnAfter().run(),
            icon: 'tabler:column-insert-right',
            labelKey: 'contextMenuTable.addColumnAfter',
        },
    ],
    [
        {
            can: (ed) => ed.can().deleteRow(),
            run: (ed) => ed.chain().focus().deleteRow().run(),
            icon: 'tabler:row-remove',
            labelKey: 'contextMenuTable.deleteRow',
        },
        {
            can: (ed) => ed.can().deleteColumn(),
            run: (ed) => ed.chain().focus().deleteColumn().run(),
            icon: 'tabler:column-remove',
            labelKey: 'contextMenuTable.deleteColumn',
        },
    ],
    [
        {
            can: (ed) => ed.can().mergeCells(),
            run: (ed) => ed.chain().focus().mergeCells().run(),
            icon: 'tabler:layout-board-split',
            labelKey: 'contextMenuTable.mergeCells',
        },
        {
            can: (ed) => ed.can().splitCell(),
            run: (ed) => ed.chain().focus().splitCell().run(),
            icon: 'tabler:layout-board-split',
            labelKey: 'contextMenuTable.splitCell',
        },
        {
            can: (ed) => ed.can().toggleHeaderRow(),
            run: (ed) => ed.chain().focus().toggleHeaderRow().run(),
            icon: 'tabler:table-row',
            labelKey: 'contextMenuTable.toggleHeaderRow',
        },
        {
            can: (ed) => ed.can().toggleHeaderColumn(),
            run: (ed) => ed.chain().focus().toggleHeaderColumn().run(),
            icon: 'tabler:table-column',
            labelKey: 'contextMenuTable.toggleHeaderColumn',
        },
    ],
];

export function ElementContextMenuPortal(): JSX.Element {
    const store = useStore<AppState>();
    const dispatch = useDispatch();
    const {duplicateElement, exportElement, updateElementFitContent, bringToFront, sendToBack, deleteElement, copyElement, cutElement} =
        useElementActions();
    const {hideAll} = useContextMenu({id: MENU_IDS.ELEMENT});

    const nav = typeof window !== 'undefined' ? window.navigator : undefined;

    return (
        <Menu id={MENU_IDS.ELEMENT}>
            {TABLE_CONTEXT_MENU_GROUPS.flatMap((group, groupIndex) => [
                ...group.map((cmd) => (
                    <Item
                        key={cmd.labelKey}
                        hidden={({props}) => !isTableV2(props as ElementMenuProps)}
                        disabled={({props}) => {
                            const p = props as ElementMenuProps | undefined;
                            const ed = p ? editorSelector(store.getState(), p.element.id) : undefined;
                            return !ed;
                        }}
                        onClick={({props}) => {
                            const p = props as ElementMenuProps;
                            const ed = editorSelector(store.getState(), p.element.id);
                            if (ed) {
                                ed.setEditable(true);
                                ed.commands.focus();
                                cmd.run(ed);
                            }
                        }}
                    >
                        <Icon className={classNames('contexify_itemContent__icon')} icon={cmd.icon} />
                        <span className="contexify_itemContent__label">{t(cmd.labelKey)}</span>
                        <RightSlot>{null}</RightSlot>
                    </Item>
                )),
                groupIndex < TABLE_CONTEXT_MENU_GROUPS.length - 1 ? (
                    <Separator key={`table-sep-${groupIndex}`} hidden={({props}) => !isTableV2(props as ElementMenuProps)} />
                ) : null,
            ])}
            <Separator hidden={({props}) => !isTableV2(props as ElementMenuProps)} />
            <Item
                onClick={({props}) => copyElement((props as ElementMenuProps).elementIndex)}
                keyMatcher={nav ? createKeyMatcher(SlideElementHotkeys.Copy.getPlatformKeys(nav)) : undefined}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:copy-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.copy')}</span>
                <RightSlot>{nav ? formatShortcuts(SlideElementHotkeys.Copy.getPlatformKeys(nav)) : null}</RightSlot>
            </Item>
            <Item
                onClick={({props}) => cutElement((props as ElementMenuProps).elementIndex)}
                keyMatcher={nav ? createKeyMatcher(SlideElementHotkeys.Cut.getPlatformKeys(nav)) : undefined}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:scissors-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.cut')}</span>
                <RightSlot>{nav ? formatShortcuts(SlideElementHotkeys.Cut.getPlatformKeys(nav)) : null}</RightSlot>
            </Item>
            <Item
                onClick={({props}) => {
                    const p = props as ElementMenuProps;
                    duplicateElement(p.elementIndex, !p.inHighlightedMode);
                }}
                keyMatcher={nav ? createKeyMatcher(SlideElementHotkeys.Duplicate.getPlatformKeys(nav)) : undefined}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:documents-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.duplicate')}</span>
                <RightSlot>{nav ? formatShortcuts(SlideElementHotkeys.Duplicate.getPlatformKeys(nav)) : null}</RightSlot>
            </Item>
            <Item
                onClick={({props}) => exportElement((props as ElementMenuProps).elementIndex)}
                keyMatcher={nav ? createKeyMatcher(SlideElementHotkeys.Export.getPlatformKeys(nav)) : undefined}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:upload-minimalistic-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.exportElement')}</span>
                <RightSlot>{nav ? formatShortcuts(SlideElementHotkeys.Export.getPlatformKeys(nav)) : null}</RightSlot>
            </Item>
            <Item
                hidden={({props}) => !(props as ElementMenuProps).inHighlightedMode}
                onClick={({props}) => {
                    const p = props as ElementMenuProps;
                    dispatch(setContextSelectedElementIndex(p.elementIndex));
                }}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:pen-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.rename')}</span>
                <RightSlot>{null}</RightSlot>
            </Item>
            <Item
                hidden={({props}) => !FIT_CONTENT_TYPES.includes((props as ElementMenuProps).element.type)}
                onClick={({props}) => {
                    const p = props as ElementMenuProps;
                    updateElementFitContent(p.elementIndex, p.element, true, false);
                }}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:minimize-bold" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.fitContent')}</span>
                <RightSlot>{null}</RightSlot>
            </Item>
            <Separator />
            <Item onClick={({props}) => bringToFront((props as ElementMenuProps).elementIndex)}>
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:notes-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.bringToFront')}</span>
                <RightSlot>{null}</RightSlot>
            </Item>
            <Item onClick={({props}) => sendToBack((props as ElementMenuProps).elementIndex)}>
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:notes-minimalistic-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.sendToBack')}</span>
                <RightSlot>{null}</RightSlot>
            </Item>
            <Separator />
            <Item
                onClick={({props}) => {
                    deleteElement((props as ElementMenuProps).elementIndex);
                    hideAll();
                }}
                keyMatcher={nav ? createKeyMatcher(SlideElementHotkeys.Delete.getPlatformKeys(nav)) : undefined}
            >
                <Icon className={classNames('contexify_itemContent__icon')} icon="solar:trash-bin-minimalistic-linear" />
                <span className="contexify_itemContent__label">{t('contextMenuElement.delete')}</span>
                <RightSlot>{nav ? formatShortcuts(SlideElementHotkeys.Delete.getPlatformKeys(nav)) : null}</RightSlot>
            </Item>
        </Menu>
    );
}
